<template>
  <div class="event-search">
    <div class="container">
      <div class="event-search__content">
        <div v-if="loading">
          <div class="event-search__loading">
            <font-awesome-icon icon="spinner"
                               spin></font-awesome-icon>
          </div>
        </div>

        <div v-else
             class="event-search__results">
          <div v-for="result in results"
               :key="result.key"
               class="event-search__result">
            <EventSearchResult v-bind="result"></EventSearchResult>
          </div>

          <div v-if="results.length === 0"
               class="event-search__no-results">
            No results
          </div>
        </div>

        <div class="event-search__pagination">
          <ProductSearchPagination :page-count="pageCount"
                                   :page-index="pageIndex"
                                   @changePage="changePage"></ProductSearchPagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { debounce } from "lodash";
import Uri from "urijs";
import EventSearchResult from "~/Components/Search/EventSearchResult.vue";
import ProductSearchPagination from "~/Components/Search/ProductSearchPagination.vue";

const uri = new Uri( window.document.location.href );
const queryString = uri.search( true );

const parameters = {
  pageIndex: 0
};

if ( queryString ) {
  if ( queryString.page ) {
    parameters.pageIndex = queryString.page - 1;
  }
}

export default {
  name: "EventSearch",
  components: {
    EventSearchResult,
    ProductSearchPagination
  },
  props: {
    nodeId: {
      type: Number,
      required: true
    },
    hideFilters: {
      type: Boolean,
      default: false
    },
    selectedMonth: {
      type: Date,
      default: null
    },
    selectedDate: {
      type: Date,
      default: null
    },
    selectedTypes: {
      type: Array,
      default: () => []
    },
    selectedSort: {
      type: String,
      default: null
    },
    selectedSubTheme: {
      type: String,
      default: null
    }
  },
  data: () => ( {
    results: [],
    resultsPerPage: 24,
    resultCount: 0,
    loading: true,
    ...parameters
  } ),
  computed: {
    pageCount () {
      let result = 0;

      if ( this.resultCount > 0 ) {
        result = Math.ceil( this.resultCount / this.resultsPerPage );
      }

      return result;
    }
  },
  watch: {
    selectedMonth: {
      immediate: true,
      handler ( val ) {
        this.pageIndex = 0;

        if ( val ) {
          const currentYear = new Date( val ).getFullYear();
          const currentMonth = new Date( val ).getMonth() + 1;
          const paddedMonth = `${currentMonth}`.padStart( 2, "0" );
          const yearMonth = `${currentYear}-${paddedMonth}`;
          this.updateQueryString( "month", yearMonth );
          this.fetchData();
        } else {
          if ( !this.loading ) {
            this.fetchData();
          }

          this.updateQueryString( "month", null );
        }
      }
    },
    selectedDate: {
      immediate: true,
      handler ( val ) {
        this.pageIndex = 0;

        if ( val ) {
          const currentYear = new Date( val ).getFullYear();
          const currentMonth = new Date( val ).getMonth() + 1;
          const currentDay = new Date( val ).getDate();

          const paddedMonth = `${currentMonth}`.padStart( 2, "0" );
          const paddedDay = `${currentDay}`.padStart( 2, "0" );

          const yearMonth = `${currentYear}-${paddedMonth}-${paddedDay}`;
          this.updateQueryString( "date", yearMonth );
          this.fetchData();
        } else {
          if ( !this.loading ) {
            this.fetchData();
          }

          this.updateQueryString( "date", null );
        }
      }
    },
    selectedSort () {
      this.pageIndex = 0;
      this.fetchData();
    },
    selectedSubTheme () {
      this.pageIndex = 0;
      this.fetchData();
    },
    pageIndex ( val ) {
      this.updateQueryString( "page", val + 1 );
      this.fetchData();
    },
    searchTerm ( val ) {
      this.handleSearchTerm( val );
    },
    selectedTypes: {
      deep: true,
      handler ( val ) {
        this.pageIndex = 0;

        const stringified = val.join( "," );
        this.updateQueryString( "filters", stringified );
        this.fetchData();
      }
    }
  },
  created () {
    this.fetchData();
  },
  methods: {
    fetchData () {
      this.loading = true;

      axios.post( "/umbraco/Surface/eventSearch/Search",
        {
          selectedTypes: this.selectedTypes
        },
        {
          params: {
            nodeId: this.nodeId,
            pageIndex: this.pageIndex,
            searchTerm: this.searchTerm,
            selectedMonth: this.selectedMonth,
            selectedDate: this.selectedDate,
            selectedSubTheme: this.selectedSubTheme,
            sort: this.selectedSort
          }
        } )
        .then( ( { data } ) => {
          this.results = data.results;
          this.resultCount = data.resultCount;
          this.resultsPerPage = data.resultsPerPage;
        } )
        .finally( () => {
          this.loading = false;
        } );
    },
    updateQueryString ( key, value ) {
      let newPageUrl = window.location.href;
      const param = {};
      param[key] = value;

      if ( value ) {
        newPageUrl = Uri( newPageUrl ).setQuery( param );
      } else {
        newPageUrl = Uri( newPageUrl ).removeQuery( key );
      }

      window.history.pushState( {}, "", newPageUrl );
    },
    changePage ( val ) {
      this.pageIndex = val;
    },
    handleSearchTerm: debounce( function handleSearchTerm ( val ) {
      this.updateQueryString( "q", val );
      this.fetchData();
    }, 200 )
  }
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.event-search {
  margin: 30px 0;

  &__content {
    padding: 0 30px 15px;

    @media ( max-width: 991px ) {
      padding: 15px 0 0;
    }
  }

  &__results {
    display: flex;
    flex-wrap: wrap;
  }

  &__result {
    flex-basis: calc( 50% - 7.5px );
    flex-grow: 0;
    flex-shrink: 0;
    display: flex;
    margin-bottom: 15px;

    &:nth-child( odd ) {
      margin-right: 7.5px;
    }

    &:nth-child( even ) {
      margin-left: 7.5px;
    }

    @media ( max-width: 991px ) {
      flex-basis: 100%;
      margin-left: 0 !important;
      margin-right: 0 !important;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__no-results {
    text-align: center;
    font-weight: 600;
    color: #006a7f;
    flex-grow: 1;
    padding-top: 30px;
    padding-bottom: 30px;
  }

  &__loading {
    text-align: center;
    font-size: 50px;
    color: @color-accent-6;
  }
}
</style>
